import _ from "lodash";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { PdfTronViewerConfig } from "./modules/PdfTronViewerConfig.js";
import WebViewer from "@pdftron/webviewer";
import pdftronWebViewerUiPackage from "@toddle-edu/pdftron-webviewer-ui/package.json";
import axios from "lib/axiosInstance";
import { PDFTRON_LICENSE_KEY } from "store/static.js";
import { Detector } from "react-detect-offline";
import {
  controlHandleConstants,
  updateAttachmentPdfTron,
} from "./modules/PdfTronViewerModule.js";
import {
  getFilteredDisableElementsInPdftron,
  PDFTRON_AUTOSAVE_TIMEOUT,
  getFileKeyAndVersionFromToddleUrl,
  isNetworkError,
  PDFTRON_ENABLE_ERASER_FLAG,
  PDFTRON_DOWNLOAD_BUTTONS,
} from "Utils/PdftronViewerHelpers.js";
import {
  cleanAttachmentObject,
  customizer,
  getpdfTronEditModeSupported,
  isXdfEqual,
} from "./modules/PdfTronViewerUtils.js";
import { uploadFileV2, downloadFromUrl } from "modules/Services";
import { generateRandomId } from "Utils/GenerateRandomId.js";
import {
  setGlobalTrayEnabledState,
  setToastMsg,
} from "Login/modules/LoginModule";
import { connect } from "react-redux";
import { compose } from "lib/apolloClient";
import ACLStore from "lib/aclStore";

import DialogBox from "./components/DialogBox";
import Loading from "UIComponents/Loading";
import I18nHOC from "UIComponents/I18nHOC";
import classes from "./PdfTronViewer.scss";
import { allowURLWithCredentials } from "Utils/SecureUrlHelpers.js";

const pdftronUiVersion = pdftronWebViewerUiPackage.version;

var annotationString = "";
const PdfTronViewer3 = React.memo(
  ({
    file,
    user,
    hidePdfTronContainer,
    invokeCustomLoader,
    studentDetails,
    pdftronViewerMode,
    onDiscardDirectly,
    closeViewer,
    useCustomLoader,
    annotationUserInfo,
    fullScreenButtonStatus,
    isDarkMode,
    showSaveButton,
    shouldShowGiveFeedbackButton,
    onPdfTronSwitchToEditMode,
    toggleShowTheatreModeV2,
    onUpdateSuccessfully,
    xfdfString,
    setPdftronInstance,
    uploadFileV2,
    updateAttachmentPdfTron,
    setToastMsg,
    downloadFromUrl,
    setGlobalTrayEnabledState,
    isGlobalTrayEnabled,
  }) => {
    const viewer = useRef(null);
    const autosaveTimeout = useRef(null);
    const [t] = useTranslation();
    const [dialogueBoxState, setDialougeBoxState] = useState("");
    const [isViewMode, setIsViewMode] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const [instance, setInstance] = useState(null);
    const [isDocumentLoaded, setIsDocumentLoaded] = useState(false);
    const [annotationsImported, setAnnotationsImported] = useState(false);
    const [commentsData, setCommentsData] = useState({});
    const [didAutosaveFail, setDidAutosaveFail] = useState(false);

    const { first_name = "", last_name = "" } = user || {};
    const fileMemoized = useMemo(() => file, [file?.id]);
    const isAutosaveEnabled = ACLStore.can("FeatureFlag:PDFTronEnableAutosave");
    const showWhiteSpaceInCommentContent = ACLStore.can(
      "FeatureFlag:PDFTronShowWhiteSpaceInCommentContent"
    );
    const shouldHideDownloadOptions = ACLStore.can(
      "FeatureFlag:PreventPrintAndScreenshots"
    );
    const shouldAllowSavingFillablePDFs = ACLStore.can(
      "FeatureFlag:PDFTronEnableSavingFillablePDFs"
    );
    const shouldUseStickyNotes = ACLStore.can(
      "FeatureFlag:PDFTronUseStickyNoteAnnotation"
    );
    const shouldFilterCommentsByPage = ACLStore.can(
      "FeatureFlag:PDFTronFilterCommentsByPage"
    );
    const shouldAdjustAnnotationsForRotation = ACLStore.can(
      "FeatureFlag:PDFTronAdjustAnnotationsForRotation"
    );
    const shouldEnableFontSizeDropdown = ACLStore.can(
      "FeatureFlag:PDFTronEnableFontSizeDropdown"
    );

    const pdfExportOptions = shouldAllowSavingFillablePDFs
      ? {
          links: true,
          widgets: true,
          fields: true,
        }
      : { links: false, widgets: false };

    const pdftronEditModeEnabled = useMemo(() => {
      return getpdfTronEditModeSupported({ file });
    }, [file]);

    useEffect(() => {
      if (
        isGlobalTrayEnabled &&
        _.includes(["STUDENT_EDITOR", "VIEWER"], pdftronViewerMode)
      ) {
        setGlobalTrayEnabledState(false);
      }

      try {
        setIsLoading(true);
        intialiseWebView();
      } catch (e) {
        setIsLoading(false);
      }
      return () => {
        // if initially global tray was enabled, enable it back on unmount
        // else keep it disabled
        if (isGlobalTrayEnabled) {
          setGlobalTrayEnabledState(true);
        }
      };
    }, []);

    useEffect(() => {
      if (
        isGlobalTrayEnabled && // if global tray is enabled only then disable
        _.includes(["TEACHER_EDITOR"], pdftronViewerMode) &&
        !isViewMode &&
        isDocumentLoaded
      ) {
        setGlobalTrayEnabledState(false);
      }
    }, [isViewMode, isDocumentLoaded]);

    useEffect(() => {
      if (instance) {
        if (
          isViewMode &&
          shouldShowGiveFeedbackButton &&
          pdftronEditModeEnabled &&
          isDocumentLoaded
        ) {
          instance.UI.enableElements(["giveFeedbackButton"]);
        } else {
          instance.UI.disableElements(["giveFeedbackButton"]);
        }

        if (shouldHideDownloadOptions) {
          if (isViewMode) {
            instance.UI.disableElements(["moreOptionsButton"]);
          } else {
            instance.UI.enableElements(["moreOptionsButton"]);
            instance.UI.disableElements(PDFTRON_DOWNLOAD_BUTTONS);
          }
        }
      }

      return () => {};
    }, [
      isViewMode,
      instance,
      shouldShowGiveFeedbackButton,
      pdftronEditModeEnabled,
      isDocumentLoaded,
    ]);

    useEffect(() => {
      if (instance) {
        setIsLoading(true);
        setIsDocumentLoaded(false);
        const {
          documentViewer,
          Tools: { ToolNames },
          annotationManager,
        } = instance.Core;

        if (file) checkAndLoadPdf({ instance });

        documentViewer.addEventListener("documentLoaded", () => {
          setIsDocumentLoaded(true);
          updateAnnotationString();
          const iframeDoc = instance.UI.iframeWindow.document;
          const header = iframeDoc.querySelector('[data-element="header"]');
          const headerTools = iframeDoc.querySelector(
            '[data-element="toolsHeader"]'
          );
          if (header) header.style.opacity = "1";

          if (headerTools) headerTools.style.opacity = "1";

          /** Set initial default tool mode to Pan(scrolling) tool. */
          instance.UI.setToolMode(ToolNames.PAN);
        });

        documentViewer.addEventListener("annotationsLoaded", () => {
          setIsLoading(false);
        });

        annotationManager.setAnnotationDisplayAuthorMap(userId => {
          if (_.isEqual(annotationUserInfo.id, userId)) {
            return `${_.get(annotationUserInfo, "first_name", "")} ${_.get(
              annotationUserInfo,
              "last_name",
              ""
            )}`;
          } else if (_.isEqual("STUDENT", annotationUserInfo.userEntityType)) {
            return "Teacher";
          } else {
            return "Student";
          }
        });

        switch (pdftronViewerMode) {
          case "TEACHER_EDITOR":
            teacherLoading({ instance });
            break;
          case "STUDENT_EDITOR":
            studentLoading({ instance });
            break;
          case "VIEWER":
            viewerLoading({ instance });
            break;
          default:
            viewerLoading({ instance });
        }
      }
      return () => {
        if (instance) {
          const { documentViewer } = instance.Core;
          documentViewer.removeEventListener("documentLoaded");
          documentViewer.removeEventListener("annotationsLoaded");
        }

        clearAutosaveTimeout();
      };
    }, [instance]);

    useEffect(() => {
      if (
        xfdfString &&
        instance &&
        isDocumentLoaded &&
        !isLoading &&
        !annotationsImported
      ) {
        const { annotationManager } = instance.Core;
        /** If importing an xfdfString, delete all existing annotations.
         * As they are downloaded each time the document is loaded using the file.url*/
        const allAnnotations = annotationManager.getAnnotationsList();
        annotationManager.deleteAnnotations(allAnnotations);

        annotationManager.importAnnotations(xfdfString);
        /** Ideally we need to import the annotations just once. Some times the documentLoaded
         * is not set to false and we run into 'importAnnotations' called before document is loaded */
        setAnnotationsImported(true);

        const comments = instance.UI?.getToddleComments?.();
        if (_.size(comments) > 0) {
          instance.UI.enableElements(["downloadAnnotatedWithoutNamesOption"]);
        } else {
          instance.UI.disableElements(["downloadAnnotatedWithoutNamesOption"]);
        }
      }
    }, [
      instance,
      xfdfString,
      isDocumentLoaded,
      isLoading,
      annotationsImported,
    ]);

    useEffect(() => {
      const handleMessageFromPDFTron = async event => {
        const message = event.data;
        switch (message) {
          case "SAVE_FILE": {
            saveDocument({
              filename: `${file.name}.pdf`,
              instance: instance,
            });
            break;
          }
          case "ANNOTATE_FILE": {
            onPdfTronSwitchToEditMode();
            changeViewToEditMode();
            break;
          }
          case "CANCEL_ANNOTATE": {
            onDiscardClick();
            break;
          }
          case "MINIMIZE":
          case "FULLSCREEN": {
            const xfdfString = await exportAnnotations();
            toggleShowTheatreModeV2?.({ xfdfString, isViewMode });
            break;
          }
          case "DOWNLOAD_ORIGINAL": {
            await downloadOriginal();
            break;
          }
        }

        if (typeof event?.data === "object") {
          switch (event?.data?.message) {
            case "CAN_DISCARD_CHANGES": {
              onDiscardClick();
              instance.UI.closeElements(["annotationHeader"]);
              break;
            }
            case "ADD_COMMENT": {
              const comment = _.get(event, "data.comment", "");
              const Id = _.get(event, "data.Id", "");
              commentsData[Id] = comment;
              setCommentsData({ ...commentsData });
              break;
            }
            case "UPDATE_COMMENT": {
              const comment = _.get(event, "data.comment", "");
              const Id = _.get(event, "data.Id", "");
              if (_.has(commentsData, Id)) {
                commentsData[Id] = comment;
                setCommentsData({ ...commentsData });
              }
              break;
            }
            case "DELETE_COMMENT": {
              const Id = _.get(event, "data.Id", "");
              if (_.has(commentsData, Id)) {
                delete commentsData[Id];
                setCommentsData({ ...commentsData });
              }
              break;
            }
          }
        }
      };

      if (file && instance) {
        const oldUrls = _.get(file, "metadata.oldUrls", []);
        if (_.isEmpty(oldUrls)) {
          instance.UI.disableElements(["downloadAnnotatedOption"]);
        } else {
          instance.UI.enableElements(["downloadAnnotatedOption"]);
        }
      }

      window.addEventListener("message", handleMessageFromPDFTron);

      return () => {
        window.removeEventListener("message", handleMessageFromPDFTron);
      };
    }, [file, instance, isViewMode]);

    useEffect(() => {
      if (!checkIfWebViewerIsReady()) return;
      loadPdfDoc({ instance });
    }, [fileMemoized]);

    useEffect(() => {
      if (useCustomLoader) {
        invokeCustomLoader?.({
          isLoading,
        });
      }
    }, [isLoading, useCustomLoader]);

    const fetchXfdfString = async xfdfData => {
      const { annotationManager } = instance.Core;
      const result = await axios.get(xfdfData);

      if (isXdfEqual(result.data, annotationString)) {
        return;
      }

      annotationManager.importAnnotations(result.data);

      updateAnnotationString();
    };

    const uploadPDFAndUpdateAttachment = async fileObj => {
      if (instance) {
        const { documentViewer } = instance.Core;
        const xfdfString = await instance.Core.annotationManager.exportAnnotations(
          pdfExportOptions
        );
        const data = await documentViewer
          .getDocument()
          .getFileData({ xfdfString });
        const arr = new Uint8Array(data);
        const pdfBlob = new Blob([arr], { type: "application/pdf" });

        /** Use the autosaved PDFs url instead of the user saved pdf urls */
        const { key, version } = getFileKeyAndVersionFromToddleUrl(
          _.get(fileObj, "metadata.autosavedUrl", "")
        );

        const url = await uploadFileV2({
          file: pdfBlob,
          attachment: fileObj,
          uploadId: generateRandomId(),
          hideToastProgress: true,
          inputFileKey: key,
        });

        let newFile = _.cloneDeep(fileObj);
        const other = {
          metadata: { oldUrls: [newFile.url], pdftronV2: true },
        };
        _.mergeWith(newFile, other, customizer);
        _.set(newFile, "url", `${url}?v=${version}`);
        _.set(newFile, "metadata.autosavedUrl", `${url}?v=${version}`);
        newFile = cleanAttachmentObject({ attachment: newFile });

        if (!_.isEqual(newFile, file)) {
          const newAttachment = await updateAttachmentPdfTron({
            newAttachment: newFile,
            hideToastProgress: true,
            showToast: false,
          });

          /** Update the original file object, in case there are subsequent autosaves */
          _.set(file, "url", newAttachment.url);
          _.set(
            file,
            "metadata.autosavedUrl",
            newAttachment.metadata.autosavedUrl
          );
          _.set(file, "metadata.oldUrls", newAttachment.metadata.oldUrls);
        }
      }
    };

    const onAutosave = async () => {
      if (autosaveTimeout.current) {
        return;
      }

      autosaveTimeout.current = setTimeout(async () => {
        try {
          if (instance) {
            const { annotationManager } = instance.Core;
            const xfdfString = await annotationManager.exportAnnotations(
              pdfExportOptions
            );

            if (isXdfEqual(annotationString, xfdfString)) {
              return;
            }

            const blob = new Blob([xfdfString], { type: "application/xml" });
            const payload = {
              name: _.get(file, "name", "xfdfFile"),
              file: blob,
              mimeType: "application/xml",
              metadata: {
                fileExtension: "xfdf",
              },
            };

            /** Use autosavedXfdfData to get the current autosave xml file,
             * so that we keep a trail of the user's saved xfdf files */
            const currentAutosavedXfdf = _.get(
              file,
              "metadata.autosavedXfdfData",
              ""
            );

            const { key, version } = getFileKeyAndVersionFromToddleUrl(
              currentAutosavedXfdf
            );

            const url = await uploadFileV2({
              file: blob,
              attachment: payload,
              uploadId: generateRandomId(),
              hideToastProgress: true,
              inputFileKey: key,
            });

            let newFile = _.cloneDeep(file);
            const existingXfdfData = _.get(file, "metadata.xfdfData", "");

            if (existingXfdfData) {
              const other = {
                metadata: {
                  oldXfdfs: [existingXfdfData],
                  pdftronV2: true,
                },
              };
              _.mergeWith(newFile, other, customizer);
            }
            _.set(newFile, "metadata.xfdfData", `${url}?v=${version}`);
            _.set(newFile, "metadata.autosavedXfdfData", `${url}?v=${version}`);
            newFile = cleanAttachmentObject({ attachment: newFile });

            if (!_.isEqual(newFile, file)) {
              const newAttachment = await updateAttachmentPdfTron({
                newAttachment: newFile,
                showToast: false,
              });

              /** Update the original file object, in case there are subsequent autosaves */
              _.set(file, "metadata.xfdfData", newAttachment.metadata.xfdfData);
              _.set(
                file,
                "metadata.autosavedXfdfData",
                newAttachment.metadata.autosavedXfdfData
              );
              _.set(file, "metadata.oldXfdfs", newAttachment.metadata.oldXfdfs);
            }

            /** Upload PDF and update the attachment again, because the PDF upload may take time */
            uploadPDFAndUpdateAttachment(newFile);
            updateAnnotationString();
          }
        } catch (err) {
          if (isNetworkError(err)) {
            setDidAutosaveFail(true);
          }
        } finally {
          clearAutosaveTimeout();
        }
      }, PDFTRON_AUTOSAVE_TIMEOUT);
    };

    const onVisibilityChange = () => {
      if (isAutosaveEnabled && _.isEqual(document.visibilityState, "hidden")) {
        onAutosave();
      }
    };

    const onBlur = () => {
      if (document.hasFocus() || !isAutosaveEnabled) {
        return;
      }
      onAutosave();
    };

    useEffect(() => {
      if (file && instance && isDocumentLoaded && !isLoading) {
        const { annotationManager } = instance.Core;
        const xfdfData = _.get(file, "metadata.xfdfData", "");
        if (xfdfData) {
          fetchXfdfString(xfdfData);
        }

        if (!isViewMode && isAutosaveEnabled) {
          annotationManager.addEventListener("annotationChanged", onAutosave);
          window.addEventListener("blur", onBlur);
          document.addEventListener("visibilitychange", onVisibilityChange);
        }
      }

      return () => {
        if (instance) {
          const { annotationManager } = instance.Core;
          if (isAutosaveEnabled) {
            annotationManager.removeEventListener(
              "annotationChanged",
              onAutosave
            );
            window.removeEventListener("blur", onBlur);
            document.removeEventListener(
              "visibilitychange",
              onVisibilityChange
            );
          }
        }
      };
    }, [file, instance, isDocumentLoaded, isLoading, isViewMode]);

    const exportAnnotations = async () => {
      const { annotationManager } = instance.Core;
      const annots = annotationManager?.getAnnotationsList();
      const xfdfString = await annotationManager.exportAnnotations({
        annotList: annots,
      });
      return xfdfString;
    };

    const downloadOriginal = async () => {
      const oldUrls = _.get(file, "metadata.oldUrls", []);
      const url = _.head(oldUrls);

      if (_.isEmpty(url)) {
        await downloadDocument({ instance });
        return;
      }
      downloadFromUrl({ downloadUrl: url, shouldAskForSave: true });
    };

    const handleNetworkChange = isOnline => {
      if (isOnline && didAutosaveFail && isAutosaveEnabled && !isViewMode) {
        onAutosave();
      }
    };

    const downloadDocument = async ({ instance }) => {
      try {
        setIsLoading(true);
        const { annotationManager, SaveOptions } = instance.Core;
        const xfdfString = await annotationManager.exportAnnotations();
        const saveOptions = SaveOptions;
        const options = {
          filename: `${_.get(file, "name", "sample")}.${_.get(
            file,
            "metadata.fileExtension",
            "pdf"
          )}`,
          xfdfString,
          flags: saveOptions.LINEARIZED,
          downloadType: "pdf",
        };

        await instance?.UI?.downloadPdf?.(options);
      } catch (err) {
        setToastMsg("toastMsgs:something_went_wrong");
      } finally {
        setIsLoading(false);
      }
    };

    const saveDocument = async ({ instance }) => {
      try {
        setIsLoading(true);
        clearAutosaveTimeout();

        const { documentViewer, annotationManager } = instance.Core;
        const isAutosaveEnabled = ACLStore.can(
          "FeatureFlag:PDFTronEnableAutosave"
        );

        const xfdfString = await annotationManager.exportAnnotations(
          pdfExportOptions
        );

        /** Check if annotationString has changed, if not, then don't create a new copy
         *  of the pdf. If it has changed, then save the updated pdf to cloud and set the
         *  new url to the attachment
         */
        if (isXdfEqual(annotationString, xfdfString)) {
          onSuccessfulSave();
          return;
        }

        const data = await documentViewer
          .getDocument()
          .getFileData({ xfdfString });
        const arr = new Uint8Array(data);
        const blob = new Blob([arr], { type: "application/pdf" });

        let xfdfDataUrl = "";
        let currentXfdfData = "";

        if (isAutosaveEnabled) {
          // Upload XFDF data
          const xfdfBlob = new Blob([xfdfString], { type: "application/xml" });
          const xfdfPayload = {
            name: _.get(file, "name", "xfdfFile"),
            file: xfdfBlob,
            mimeType: "application/xml",
            metadata: {
              fileExtension: "xfdf",
            },
          };

          currentXfdfData = _.get(file, "metadata.xfdfData", "");

          try {
            xfdfDataUrl = await uploadFileV2({
              file: xfdfBlob,
              attachment: xfdfPayload,
              uploadId: generateRandomId(),
              hideToastProgress: true,
            });
          } catch (error) {
            console.error("Failed to upload XFDF data:", error);
          }
        }

        const url = await uploadFileV2({
          file: blob,
          attachment: file,
          uploadId: generateRandomId(),
        });

        let newFile = _.cloneDeep(file);
        const other = {
          metadata: {
            oldUrls: [newFile.url],
            ...(isAutosaveEnabled
              ? { xfdfData: xfdfDataUrl }
              : {}) /** Stores the annotations in xml format */,
            pdftronV2: true,
          },
        };
        _.mergeWith(newFile, other, customizer);
        /** Add currentXfdfData in oldXfdfs */
        if (currentXfdfData && isAutosaveEnabled) {
          const extraMetadata = {
            metadata: {
              oldXfdfs: [currentXfdfData],
            },
          };
          _.mergeWith(newFile, extraMetadata, customizer);
        }
        _.set(newFile, "url", url);
        newFile = cleanAttachmentObject({ attachment: newFile });

        await updateAttachmentPdfTron({
          newAttachment: newFile,
        });

        onSuccessfulSave(commentsData);
        setCommentsData({});
        setGlobalTrayEnabledState(true);
      } catch (err) {
        setIsLoading(false);
        setToastMsg("toastMsgs:something_went_wrong");
      }
    };

    const onSuccessfulSave = newComments => {
      onUpdateSuccessfully?.(newComments);
      if (_.isEqual("TEACHER_EDITOR", pdftronViewerMode)) {
        onDiscardDirectly?.();
        changeEditToViewMode({ instance });
      } else {
        closeViewer();
      }
      setIsLoading(false);
    };

    const checkAndLoadPdf = useCallback(
      async ({ instance }) => {
        const { PDFNet } = instance.Core;

        try {
          await instance.Core.PDFNet.initialize(PDFTRON_LICENSE_KEY);

          const pdfDocument = await PDFNet.PDFDoc.createFromURL(file.url, {
            withCredentials: allowURLWithCredentials(file.url),
          });

          pdfDocument.isEncrypted().then(data => {
            if (data) {
              setIsLoading(false);
            }
          });

          instance.UI.loadDocument(pdfDocument, {
            filename: `${_.get(file, "name", "sample")}.${_.get(
              file,
              "metadata.fileExtension",
              "pdf"
            )}`,
          });
        } catch (err) {
          if (_.isEqual(file?.type, "FILE")) {
            setToastMsg("toastMsgs:invalid_pdf_file");
          }
          setIsLoading(false);
        }
      },
      [file]
    );

    const teacherLoading = ({ instance }) => {
      const { annotationManager } = instance.Core;

      annotationManager.enableReadOnlyMode();

      // Allow teacher to edit/remove all annotations
      annotationManager.promoteUserToAdmin();

      if (showSaveButton) {
        onPdfTronSwitchToEditMode();
        changeViewToEditMode();
      } else {
        if (showSaveButton !== undefined) {
          onDiscardDirectly?.();
        }

        const filteredDisableElements = getFilteredDisableElementsInPdftron(
          PdfTronViewerConfig[pdftronViewerMode]["PERMANENT_DISABLE_ELEMENTS"]
        );

        instance.UI.disableElements(filteredDisableElements);
      }
    };

    const studentLoading = ({ instance }) => {
      const { annotationManager } = instance.Core;

      annotationManager.enableReadOnlyMode();
      instance.UI.setToolbarGroup("selectToolButton");

      const filteredDisableElements = getFilteredDisableElementsInPdftron(
        PdfTronViewerConfig[pdftronViewerMode]["PERMANENT_DISABLE_ELEMENTS"]
      );

      instance.UI.disableElements(filteredDisableElements);

      if (shouldShowGiveFeedbackButton && pdftronEditModeEnabled) {
        changeViewToEditMode();
      }
    };

    const viewerLoading = ({ instance }) => {
      const { annotationManager } = instance.Core;

      annotationManager.enableReadOnlyMode();

      const filteredDisableElements = getFilteredDisableElementsInPdftron(
        PdfTronViewerConfig[pdftronViewerMode]["PERMANENT_DISABLE_ELEMENTS"]
      );

      instance.UI.disableElements(filteredDisableElements);
    };

    const intialiseWebView = async () => {
      const instance = await WebViewer(
        {
          path: `/webviewer/${pdftronUiVersion}`,
          l: PDFTRON_LICENSE_KEY,
          annotationUser: annotationUserInfo.id,
          fullAPI: true,
          initialDoc: "",
          custom: JSON.stringify({
            isToddleCustomView: true,
            username: `${first_name} ${last_name}`,
            fullScreenButtonStatus,
            showEraserButton: ACLStore.can(PDFTRON_ENABLE_ERASER_FLAG),
            showWhiteSpaceInCommentContent,
            useStickyNotes: shouldUseStickyNotes,
            filterCommentsByPage: shouldFilterCommentsByPage,
            adjustAnnotationForRotation: shouldAdjustAnnotationsForRotation,
            enableFontSizeDropdown: shouldEnableFontSizeDropdown,
          }),
        },
        viewer.current
      );
      instance?.Core?.disableEmbeddedJavaScript?.();
      const iframeDoc = instance.UI.iframeWindow.document;
      const header = iframeDoc.querySelector('[data-element="header"]');
      const headerTools = iframeDoc.querySelector(
        '[data-element="toolsHeader"]'
      );
      if (header) header.style.opacity = "0";
      if (headerTools) headerTools.style.opacity = "0";
      if (isDarkMode == true) instance.UI.setTheme("dark");
      instance.Core.documentViewer.enableStylusMode?.();
      setInstance(instance);
      setPdftronInstance?.({ instance });
      if (file) {
        await loadPdfDoc({ instance });
      }

      /** Custom control handles around each annotation */
      const { Annotations, annotationManager } = instance.Core;
      const { SelectionModel, ControlHandle } = Annotations;

      const {
        outlineColor,
        color,
        handleHeight,
        handleWidth,
        selectionOutlineThickness,
        defaultNoPermissionSelectionOutlineColor,
        defaultSelectionOutlineColor,
      } = controlHandleConstants;

      ControlHandle.outlineColor = outlineColor;
      ControlHandle.color = color;
      ControlHandle.handleWidth = handleWidth;
      ControlHandle.handleHeight = handleHeight;

      SelectionModel.selectionOutlineThickness = selectionOutlineThickness;
      SelectionModel.defaultSelectionOutlineColor = defaultSelectionOutlineColor;
      SelectionModel.defaultNoPermissionSelectionOutlineColor = defaultNoPermissionSelectionOutlineColor;

      annotationManager.on("annotationChanged", (annotations, action) => {
        if (action === "add") {
          annotations.forEach(annotation => {
            annotation.setRotationControlEnabled(false);
          });
        }
      });
    };

    const pdfTronClasses = classNames(
      { [classes.hideContainer]: hidePdfTronContainer },
      {
        [classes.pdftron]: true,
      }
    );

    /* DialogBox utils  start here*/
    const studentName = _.get(studentDetails, "firstName", "student");

    const onClickDailogButton1 = () => {
      setDialougeBoxState("");
    };

    const onClickDailogButton2 = () => {
      if (_.isEqual("FEEDBACK", dialogueBoxState)) {
        changeViewToEditMode();
        setDialougeBoxState("");
      } else if (_.isEqual("DISCARD", dialogueBoxState)) {
        onDiscardClick();
      }
    };

    const checkIfWebViewerIsReady = () => {
      if (file && instance) {
        return true;
      }
      return false;
    };

    const loadPdfDoc = async ({ instance }) => {
      if (!checkIfWebViewerIsReady()) return;
      setIsLoading(true);
      if (instance?.documentViewer?.getDocument?.()) {
        await instance.UI.closeDocument();
      }
      //re-initialize license as old document is cloded
      await instance.Core.PDFNet.initialize(PDFTRON_LICENSE_KEY);
      await loadPdfDocumentFromFile();
      setIsLoading(false);
    };

    const changeViewToEditMode = () => {
      setIsViewMode(false);

      if (instance) {
        const { annotationManager } = instance.Core;

        annotationManager.disableReadOnlyMode();

        instance.UI.disableElements(["giveFeedbackButton"]);

        instance.UI.enableElements(
          PdfTronViewerConfig[pdftronViewerMode]["TOGGLE_ELEMENTS"]
        );

        instance.UI.setToolbarGroup("toolbarGroup-Shapes");
        setTimeout(() => {
          //scroll page to top after load
          const scrollDomElement = instance.UI?.iframeWindow?.document?.querySelector(
            '[data-element="documentContainer"]'
          );
          if (scrollDomElement) scrollDomElement.scroll(0, 0);
          updateAnnotationString();
        }, 1);

        const {
          Tools: { ToolNames },
        } = instance.Core;
        instance.UI.setToolMode(ToolNames.EDIT);
      }
    };

    const changeEditToViewMode = ({ instance }) => {
      if (instance) {
        const {
          annotationManager,
          Tools: { ToolNames },
        } = instance.Core;

        annotationManager.enableReadOnlyMode();

        instance.UI.disableElements(
          PdfTronViewerConfig[pdftronViewerMode]["TOGGLE_ELEMENTS"]
        );
        instance.UI.setToolMode(ToolNames.PAN);
      }
      setIsViewMode(true);
    };

    const resetDocument = async ({ instance }) => {
      annotationString = "";
      if (instance && instance.UI.loadDocument && file) {
        setIsLoading(true);
        try {
          const { PDFNet } = instance.Core;
          const pdfDocument = await PDFNet.PDFDoc.createFromURL(file.url, {
            withCredentials: allowURLWithCredentials(file.url),
          });
          await instance.UI.loadDocument(pdfDocument, {
            filename: `${_.get(file, "name", "sample")}.${_.get(
              file,
              "metadata.fileExtension",
              "pdf"
            )}`,
          });
        } catch (e) {
        } finally {
          setIsLoading(false);
        }
      }
    };

    const clearAutosaveTimeout = () => {
      if (autosaveTimeout.current) {
        clearTimeout(autosaveTimeout.current);
        autosaveTimeout.current = null;
      }
    };

    const onDiscardClick = () => {
      if (_.isEqual("TEACHER_EDITOR", pdftronViewerMode)) {
        onDiscardDirectly?.();
        changeEditToViewMode({ instance });

        resetDocument({ instance });
      } else {
        closeViewer();
      }

      clearAutosaveTimeout();
    };

    const loadPdfDocumentFromFile = useCallback(async () => {
      const fileUrl = _.get(file, "url", "");
      const fileName = _.get(file, "name", "sample");
      const fileExtension = _.get(file, "metadata.fileExtension", "pdf");
      if (instance && fileUrl) {
        const { PDFNet } = instance.Core;
        const pdfDocument = await PDFNet.PDFDoc.createFromURL(fileUrl, {
          withCredentials: allowURLWithCredentials(fileUrl),
        });
        await instance.UI.loadDocument(pdfDocument, {
          filename: `${fileName}.${fileExtension}`,
        });
      }
    }, [instance, file]);

    const updateAnnotationString = async () => {
      if (instance) {
        const { annotationManager } = instance.Core;
        annotationString = await annotationManager.exportAnnotations(
          pdfExportOptions
        );
      }
    };

    return (
      <div className={pdfTronClasses}>
        <div className={classes.webviewer} ref={viewer}></div>

        <Detector
          polling={false}
          render={() => null}
          onChange={handleNetworkChange}
        />

        {dialogueBoxState && (
          <DialogBox
            dialogueBoxState={dialogueBoxState}
            t={t}
            studentName={studentName}
            onClickButton1={onClickDailogButton1}
            toggleDialogueBoxDisplay={onClickDailogButton1}
            onClickButton2={onClickDailogButton2}
          />
        )}

        {!hidePdfTronContainer && !useCustomLoader && isLoading && (
          <div className={classes.loader}>
            <Loading />
          </div>
        )}
      </div>
    );
  }
);

PdfTronViewer3.displayName = "PdfTronViewer3";

const mapStateToProps = state => {
  const user = state.login.userInfo;
  const isGlobalTrayEnabled = _.get(state, "login.isGlobalTrayEnabled", true);
  return {
    user,
    isGlobalTrayEnabled,
  };
};

const mapActionCreators = {
  uploadFileV2,
  updateAttachmentPdfTron,
  setToastMsg,
  downloadFromUrl,
  setGlobalTrayEnabledState,
};

PdfTronViewer3.defaultProps = {
  onPdfTronSwitchToEditMode: () => {},
  pdftronViewerMode: "VIEWER",
  closeViewer: () => {},
};

export default compose(
  I18nHOC({ resource: ["common"] }),
  connect(mapStateToProps, mapActionCreators)
)(PdfTronViewer3);
